import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as ImageExportIcon } from "../assets/svgs/image-export-icon.svg";
import { useGetPinsCachedQuery, useGetViewBoardsQuery } from "../store/api.ts";
import MarkdownResponse from "./MarkdownResponse";
const FLOW_SCREENS = {
  METHOD: "method",
  PHOTO: "photo",
  PRODUCTS: "products",
  SUMMARY: "summary",
  GENERATING: "generating",
};

const normalizeBoardsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.boards)) return data.boards;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

const normalizePinsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.pins)) return data.pins;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

const getValidImage = (...images) => {
  return (
    images.find((image) => {
      if (!image) return false;

      const value = String(image);

      return (
        value !== "null" &&
        value !== "undefined" &&
        !value.endsWith("/null") &&
        !value.includes("/null")
      );
    }) || ""
  );
};

const normalizePinnedProduct = (pin, fallbackBoardSlug = "") => {
  const product = pin?.product || {};
  const firstVariant = Array.isArray(product?.variants)
    ? product.variants[0]
    : null;

  const slug =
    product?.real_slug ||
    product?.slug ||
    product?.product_url ||
    pin?.slug ||
    "";

  return {
    id: product?.id || pin?.pin_id,

    productId: product?.id || null,
    productType:
      product?.product_type ||
      product?.productType ||
      (product?.is_marketplace === false ? "product" : "marketplace"),

    pinId: pin?.pin_id,
    boardId: pin?.board_id,

    boardSlug: String(
      pin?.board_slug || pin?.board?.slug || fallbackBoardSlug || "",
    ),

    name: product?.product_name || product?.name || product?.brand || "Product",

    category:
      product?.garment_type ||
      product?.category ||
      product?.product_type ||
      "Item",

    brand: product?.brand || "",
    size: product?.size || firstVariant?.size || "",

    price:
      product?.discounted_price ??
      product?.price ??
      firstVariant?.discounted_price ??
      firstVariant?.price ??
      "",

    image: getValidImage(
      product?.image_url,
      product?.image,
      product?.main_images?.[0],
      product?.images?.[0],
      firstVariant?.image,
      firstVariant?.image_url,
    ),

    slug,
  };
};

const getProductKey = (item) => {
  const productType = item?.productType || item?.product_type || "product";

  const productId = item?.productId ?? item?.product_id ?? item?.id;

  if (productId !== null && productId !== undefined && productId !== "") {
    return `${productType}:id:${productId}`;
  }

  const slug = String(item?.real_slug || item?.slug || item?.product_url || "")
    .trim()
    .toLowerCase();

  if (slug) {
    return `${productType}:slug:${slug}`;
  }

  return [
    productType,
    String(item?.name || "")
      .trim()
      .toLowerCase(),
    String(item?.brand || "")
      .trim()
      .toLowerCase(),
    String(item?.image || "")
      .trim()
      .toLowerCase(),
  ].join(":");
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === "") {
    return "";
  }

  const stringPrice = String(price).trim();

  if (stringPrice.startsWith("$")) {
    return stringPrice;
  }

  const numericPrice = Number(stringPrice.replace(/,/g, ""));

  if (!Number.isFinite(numericPrice)) {
    return stringPrice;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: numericPrice % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericPrice);
};

const ContinueArrow = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
    aria-hidden="true"
  >
    <path
      d="M4 10H16M16 10L11.5 5.5M16 10L11.5 14.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MethodOption = ({ selected, eyebrow, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={[
      "relative w-full rounded-[5px] border-[2px] bg-white px-4 py-4 text-left transition",
      selected
        ? "border-[#8F333BCC]"
        : "border-[#E4E1DE] hover:border-[#A7A29D]",
    ].join(" ")}
  >
    <div className="pr-10 pl-3">
      <p
        className={[
          "font-spaceMono text-[10px] uppercase tracking-[0.12em]",
          selected ? "text-[#F28C80]" : "text-[#5C5C5C]",
        ].join(" ")}
      >
        {eyebrow}
      </p>

      <h3 className="mt-1 text-[22px] font-medium leading-tight text-[#1E1E1E] font-helvena">
        {title}
      </h3>

      <p className="mt-1 text-[12px] leading-[1.45] text-[#5C5C5C] font-helvena font-medium">
        {description}
      </p>
    </div>

    <span
      className={[
        "absolute right-4 top-1/2 grid h-[18px] w-[18px] -translate-y-1/2 place-items-center rounded-full border text-[11px]",
        selected
          ? "border-[#8F333BCC] bg-[#8F333BCC] text-white"
          : "border-[#DDD9D5] bg-white text-transparent",
      ].join(" ")}
    >
      ✓
    </span>
  </button>
);

function VirtualTryOnFlowModal({
  focalProduct,
  isAuthenticated,
  surveyImageUrl,
  isSurveyImageFetching,
  uploadedPreview,
  selectedPhotoSource,
  onSelectPhotoSource,
  onFileChange,
  onExpressContinue,
  onOutfitReady,
  onClose,
  isUploading,
  isMultiTryOnGenerating,
  multiTryOnStream,
}) {
  const [screen, setScreen] = useState(FLOW_SCREENS.METHOD);
  const [selectedMode, setSelectedMode] = useState("");
  const [selectedBoardSlug, setSelectedBoardSlug] = useState("");
  const [selectedOutfitItems, setSelectedOutfitItems] = useState([]);

  const shouldLoadBoards =
    selectedMode === "outfit" &&
    [FLOW_SCREENS.PRODUCTS, FLOW_SCREENS.SUMMARY].includes(screen);

  const {
    data: boardsResponse,
    isLoading: areBoardsLoading,
    isFetching: areBoardsFetching,
    isError: areBoardsError,
    refetch: refetchBoards,
  } = useGetViewBoardsQuery(undefined, {
    skip: !shouldLoadBoards,
  });

  const boards = useMemo(
    () => normalizeBoardsResponse(boardsResponse),
    [boardsResponse],
  );

  useEffect(() => {
    if (!shouldLoadBoards) return;
    if (selectedBoardSlug) return;

    const firstBoard = boards.find((board) => board?.slug);

    if (firstBoard?.slug) {
      setSelectedBoardSlug(String(firstBoard.slug));
    }
  }, [boards, selectedBoardSlug, shouldLoadBoards]);

  const selectedBoard = useMemo(() => {
    return (
      boards.find((board) => String(board?.slug || "") === selectedBoardSlug) ||
      null
    );
  }, [boards, selectedBoardSlug]);

  const {
    data: pinsResponse,
    isLoading: areProductsLoading,
    isFetching: areProductsFetching,
    isError: areProductsError,
    refetch: refetchProducts,
  } = useGetPinsCachedQuery(
    {
      board_slug: selectedBoardSlug,
    },
    {
      skip: !shouldLoadBoards || !selectedBoardSlug,
    },
  );

  const boardProducts = useMemo(() => {
    const normalizedProducts = normalizePinsResponse(pinsResponse).map((pin) =>
      normalizePinnedProduct(pin, selectedBoardSlug),
    );

    const uniqueProducts = new Map();

    normalizedProducts.forEach((product) => {
      const productKey = getProductKey(product);

      if (!uniqueProducts.has(productKey)) {
        uniqueProducts.set(productKey, product);
      }
    });

    return Array.from(uniqueProducts.values());
  }, [pinsResponse, selectedBoardSlug]);

  const selectedProductKeys = useMemo(() => {
    return new Set(selectedOutfitItems.map(getProductKey));
  }, [selectedOutfitItems]);

  const currentStep =
    screen === FLOW_SCREENS.METHOD
      ? 1
      : [FLOW_SCREENS.SUMMARY, FLOW_SCREENS.GENERATING].includes(screen)
        ? 3
        : 2;

  const isBusy = isUploading || isMultiTryOnGenerating;

  const toggleOutfitItem = (product) => {
    const productKey = getProductKey(product);

    setSelectedOutfitItems((currentItems) => {
      const isAlreadySelected = currentItems.some(
        (item) => getProductKey(item) === productKey,
      );

      if (isAlreadySelected) {
        return currentItems.filter(
          (item) => getProductKey(item) !== productKey,
        );
      }

      return [...currentItems, product];
    });
  };

  const handleChangeOutfitItem = (item) => {
    const itemBoardSlug = String(item?.boardSlug || "");

    if (itemBoardSlug) {
      setSelectedBoardSlug(itemBoardSlug);
    }

    setScreen(FLOW_SCREENS.PRODUCTS);
  };

  const handleBack = () => {
    if (isBusy) return;

    if (screen === FLOW_SCREENS.GENERATING) {
      setScreen(
        isExpressTryOn
          ? FLOW_SCREENS.PHOTO
          : FLOW_SCREENS.SUMMARY,
      );
      return;
    }

    if (screen === FLOW_SCREENS.SUMMARY) {
      setScreen(FLOW_SCREENS.PRODUCTS);
      return;
    }

    setScreen(FLOW_SCREENS.METHOD);
  };

  const handleContinue = async () => {
    if (screen === FLOW_SCREENS.METHOD) {
      if (!selectedMode) return;

      setScreen(
        selectedMode === "express" ? FLOW_SCREENS.PHOTO : FLOW_SCREENS.PRODUCTS,
      );

      return;
    }

    if (screen === FLOW_SCREENS.PHOTO) {
      const didStart = await onExpressContinue?.();
      if (didStart !== false) {
        setScreen(FLOW_SCREENS.GENERATING);
      }
      return;
    }

    if (screen === FLOW_SCREENS.PRODUCTS) {
      if (!selectedOutfitItems.length) return;

      setScreen(FLOW_SCREENS.SUMMARY);
      return;
    }

    if (screen === FLOW_SCREENS.SUMMARY) {
      const didStart = await onOutfitReady?.({
        board: selectedBoard,
        products: selectedOutfitItems,
      });
      if (didStart !== false) {
        setScreen(FLOW_SCREENS.GENERATING);
      }
      return;
    }
    if (screen === FLOW_SCREENS.GENERATING) {
      if (multiTryOnStream?.error) {
        setScreen(
          isExpressTryOn
            ? FLOW_SCREENS.PHOTO
            : FLOW_SCREENS.SUMMARY,
        );
        return;
      }
      if (multiTryOnStream?.done && multiTryOnStream?.final_image) {
        onClose?.();
      }
    }
  };

  const isContinueDisabled =
    isBusy ||
    (screen === FLOW_SCREENS.METHOD && !selectedMode) ||
    (screen === FLOW_SCREENS.PHOTO && !selectedPhotoSource) ||
    (screen === FLOW_SCREENS.PRODUCTS && selectedOutfitItems.length === 0) ||
    (screen === FLOW_SCREENS.GENERATING &&
      !multiTryOnStream?.error &&
      !(multiTryOnStream?.done && multiTryOnStream?.final_image));

  const summaryItems = selectedOutfitItems.map((item) => ({
    ...item,
    summaryCategory: item.category || "Item",
  }));

  const streamedTryOnImage = multiTryOnStream?.final_image || multiTryOnStream?.partial_image || "";
  const isFinalTryOnImage = Boolean(multiTryOnStream?.final_image);
  const isExpressTryOn = selectedMode === "express";
  const currentStreamingMessage = String(multiTryOnStream?.message || "").trim() || (multiTryOnStream?.done ? "Complete." : "Preparing your outfit...");
  const multiTryOnErrorMessage = multiTryOnStream?.error || "Multi try-on failed. Please try again.";

  return (
    <div className="absolute inset-0 z-30 overflow-hidden rounded-[6px] border border-[#E2DFDB] bg-white shadow-[0_18px_60px_rgba(0,0,0,0.28)]">
      <div className="flex h-full min-h-0 flex-col px-4 pb-3 pt-4 sm:px-6 sm:pb-4 sm:pt-5">
        <div className="flex shrink-0 items-start justify-between gap-4">
          <div className="flex flex-row items-center gap-[11px] justify-center mt-2">
            <span className="flex text-center items-center justify-center h-[18px] w-[18px] rounded-full bg-[#1E1E1E] font-spaceMono font-bold text-[9px] text-white">
              {currentStep}
            </span>

            <span className="font-spaceMono text-[11px] uppercase tracking-[0.17em] text-[#5C5C5C]">
              Step {String(currentStep).padStart(2, "0")} of 03
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close virtual try-on"
            className="grid h-8 w-8 place-items-center rounded-full bg-[#FAFAFA] text-[17px] leading-none text-[#252525] transition hover:bg-[#F0F0F0] disabled:cursor-not-allowed disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <div className={`min-h-0 flex-1 pb-[10px] pt-[10px] ${screen === FLOW_SCREENS.SUMMARY ? 'overflow-y-auto ' : 'overflow-y-auto sm:overflow-y-visible'}`}>
          {screen === FLOW_SCREENS.METHOD ? (
            <div>
              <h2 className="max-w-[470px] text-[27px] font-medium leading-[1.05] text-[#1E1E1E] sm:text-[34px] font-helvena">
                How would you like to try this?
              </h2>

              <p className="mt-3 max-w-[460px] text-[14px] leading-[1.5] text-[#5C5C5C] font-medium">
                Preview the product solo, or build a complete outfit using items
                from your Boards.
              </p>

              <div className="mt-[10px] space-y-[10px]">
                <MethodOption
                  selected={selectedMode === "express"}
                  eyebrow="Solo preview"
                  title="Express Try-On"
                  description={`See the ${
                    focalProduct?.name || "product"
                  } on its own with a quick AI preview.`}
                  onClick={() => setSelectedMode("express")}
                />

                <MethodOption
                  selected={selectedMode === "outfit"}
                  eyebrow="Style an outfit"
                  title="Make my outfit"
                  description="Build a complete look from your Boards using your survey body image."
                  onClick={() => setSelectedMode("outfit")}
                />
              </div>
            </div>
          ) : null}

          {screen === FLOW_SCREENS.PHOTO ? (
            <div>
              <h2 className="text-[26px] md:text-[34px] font-medium leading-tight text-[#1E1E1E] font-helvena">
                Upload Your Photo
              </h2>

              <p className="mt-2 text-[13px] font-helvena font-medium text-[#5C5C5C]">
                Select your survey image or upload another full-body photo.
              </p>

              <div className="mt-5">
                <p className="text-[12px] font-normal text-[#0A0A0A] font-helvena">
                  Use Survey Image
                </p>

                <button
                  type="button"
                  onClick={() => onSelectPhotoSource("survey")}
                  disabled={!isAuthenticated || isSurveyImageFetching || isBusy}
                  className={[
                    "mt-3 flex h-[112px] w-[112px] items-center justify-center overflow-hidden rounded-[4px] bg-[#999999] text-center text-[12px] text-white transition disabled:cursor-not-allowed disabled:opacity-60",
                    selectedPhotoSource === "survey"
                      ? "border-4 border-[#252525] ring-2 ring-white"
                      : "border border-transparent",
                  ].join(" ")}
                >
                  {isSurveyImageFetching ? (
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : surveyImageUrl ? (
                    <img
                      src={surveyImageUrl}
                      alt="Survey full body"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    "Survey Image"
                  )}
                </button>
              </div>

              <div className="my-3 flex items-center gap-4">
                <div className="h-px flex-1 bg-[#E5E7EB]" />

                <span className="font-spaceMono text-[9px] uppercase text-[#6A7282]">
                  or
                </span>

                <div className="h-px flex-1 bg-[#E5E7EB]" />
              </div>

              <div>
                <p className="text-[12px] font-normal text-[#0A0A0A] font-helvena">
                  Upload Another
                </p>

                <label
                  className={[
                    "mt-3 flex min-h-[145px] cursor-pointer flex-col items-center justify-center rounded-[4px] border border-dashed bg-white px-4 py-5 text-center transition hover:bg-[#FAFAFA]",
                    selectedPhotoSource === "upload"
                      ? "border-[#252525] ring-2 ring-[#252525]/20"
                      : "border-[#C9CED6]",
                  ].join(" ")}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={onFileChange}
                    className="hidden"
                    disabled={isBusy}
                  />

                  {uploadedPreview ? (
                    <img
                      src={uploadedPreview}
                      alt="Uploaded try-on preview"
                      className="h-[105px] w-[105px] rounded-[4px] object-cover"
                    />
                  ) : (
                    <>
                      <ImageExportIcon />

                      <span className="mt-2 text-[12px] font-medium text-[#0A0A0A]">
                        Choose a photo
                      </span>

                      <span className="mt-2 font-spaceMono text-[9px] text-[#6A7282]">
                        PNG, JPG up to 10MB
                      </span>
                    </>
                  )}
                </label>
              </div>
            </div>
          ) : null}

          {screen === FLOW_SCREENS.PRODUCTS ? (
            <div>
              <h2 className="text-[28px] md:text-[34px] font-medium leading-tight text-[#1E1E1E] font-helvena">
                Pair with more
              </h2>

              <p className="mt-2 text-[13px] font-helvena text-[#5C5C5C]">
                Choose one piece for each category. Items are pulled from your
                Boards.
              </p>

              {areBoardsLoading || areBoardsFetching ? (
                <div className="mt-8 flex items-center gap-2 text-[12px] text-[#777]">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#DADADA] border-t-[#333]" />
                  Loading boards...
                </div>
              ) : areBoardsError ? (
                <div className="mt-6 rounded-[5px] border border-[#E7E3DF] bg-[#FAFAFA] p-4">
                  <p className="text-[12px] text-[#555]">
                    Failed to load your Boards.
                  </p>

                  <button
                    type="button"
                    onClick={refetchBoards}
                    className="mt-3 text-[12px] font-medium text-[#FF6333]"
                  >
                    Retry
                  </button>
                </div>
              ) : boards.length === 0 ? (
                <div className="mt-6 rounded-[5px] border border-[#E7E3DF] bg-[#FAFAFA] p-5 text-center">
                  <p className="text-[13px] font-medium text-[#252525]">
                    No Boards found
                  </p>

                  <p className="mt-1 text-[11px] text-[#777]">
                    Save products to a Board before building an outfit.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                    {boards.map((board) => {
                      const boardSlug = String(board?.slug || "");
                      const isSelected = boardSlug === selectedBoardSlug;

                      return (
                        <button
                          key={boardSlug || board?.id}
                          type="button"
                          onClick={() => setSelectedBoardSlug(boardSlug)}
                          className={[
                            "shrink-0 rounded-[5px] border px-3 py-2 text-[10px] uppercase transition h-[37px] font-medium font-helvena border-[#EDEDED]",
                            isSelected
                              ? "bg-[#8F333BCC] text-white"
                              : "bg-white text-[#1E1E1E] hover:border-[#999]",
                          ].join(" ")}
                        >
                          {board?.name || "Board"}{" "}
                          <span
                            className={[
                              "ml-1  text-[10px] font-spaceMono",
                              isSelected ? "text-white" : "text-[#5C5C5C]",
                            ]}
                          >
                            {Number(board?.pin_count || 0)}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {areProductsLoading || areProductsFetching ? (
                    <div className="mt-8 flex items-center gap-2 text-[12px] text-[#777]">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#DADADA] border-t-[#333]" />
                      Loading products...
                    </div>
                  ) : areProductsError ? (
                    <div className="mt-5 rounded-[5px] border border-[#E7E3DF] bg-[#FAFAFA] p-4">
                      <p className="text-[12px] text-[#555]">
                        Failed to load products from this Board.
                      </p>

                      <button
                        type="button"
                        onClick={refetchProducts}
                        className="mt-3 text-[12px] font-medium text-[#FF6333]"
                      >
                        Retry
                      </button>
                    </div>
                  ) : boardProducts.length === 0 ? (
                    <div className="mt-5 rounded-[5px] border border-[#E7E3DF] bg-[#FAFAFA] p-5 text-center">
                      <p className="text-[12px] text-[#666]">
                        This Board does not contain any products.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-flow-col auto-cols-[145px] gap-3 overflow-x-auto pb-3 sm:auto-cols-[calc((100%-24px)/3)]">
                      {boardProducts.map((product) => {
                        const productKey = getProductKey(product);
                        const isSelected = selectedProductKeys.has(productKey);

                        return (
                          <button
                            key={productKey}
                            type="button"
                            onClick={() => toggleOutfitItem(product)}
                            aria-pressed={isSelected}
                            className="group relative min-w-0 text-left"
                          >
                            <div
                              className={[
                                "relative h-[228px] min-w-[155px] overflow-hidden rounded-[5px] border bg-[#EAEAEA] transition",
                                isSelected
                                  ? "border-[#8F333BCC] ring-2 ring-[#8F333BCC]/25"
                                  : "border-transparent",
                              ].join(" ")}
                            >
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="grid h-full place-items-center bg-[#555] text-[11px] text-white">
                                  Product
                                </div>
                              )}

                              {isSelected ? (
                                <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-[#8F333BCC] text-[11px] text-white">
                                  ✓
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2 flex items-start justify-between gap-2 font-helvena font-normal text=[14px]">
                              <p className="line-clamp-1 min-w-0 text-[11px] leading-[1.35] text-[#252525]">
                                {product.name}
                              </p>

                              <p className="shrink-0 text-[11px] text-[#252525]">
                                {formatPrice(product.price)}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {selectedOutfitItems.length > 0 ? (
                <p className="mt-2 font-spaceMono text-[9px] uppercase tracking-[0.12em] text-[#6B6661]">
                  {selectedOutfitItems.length} item
                  {selectedOutfitItems.length === 1 ? "" : "s"} selected
                </p>
              ) : null}
            </div>
          ) : null}

          {screen === FLOW_SCREENS.SUMMARY ? (
            <div>
              <h2 className="text-[29px] md:text-[34px] font-helvena font-medium uppercase leading-tight text-[#1E1E1E]">
                Generate
              </h2>

              <p className="mt-2 text-[13px] text-[#5C5C5C] font-helvena font-medium">
                Review the selected pieces before generating your outfit.
              </p>

              <div className="mt-5 space-y-2">
                {summaryItems.map((item) => (
                  <div
                    key={getProductKey(item)}
                    className="flex min-h-[80px] items-center gap-3 rounded-[5px] border border-[#EDEDED] bg-white px-[10px]"
                  >
                    <div className="h-[80px] w-[80px] shrink-0 overflow-hidden rounded-[6px] bg-[#3B3A3A]">
                      {item?.image ? (
                        <img
                          src={item.image}
                          alt={item?.name || "Selected product"}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-spaceMono text-[9px] uppercase tracking-[0.12em] text-[#5C5C5C] font-normal">
                        {item.summaryCategory}
                      </p>
                      <p className="mt-1 truncate text-[14px] font-medium text-[#1E1E1E] font-helvena">
                        {item?.name || "Product"}
                      </p>
                      <p className="mt-1 text-[9px] text-[#5F5A55]">
                        {[item?.brand, item?.size].filter(Boolean).join(" · ")}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="text-[13px] font-medium font-helvena text-[#1E1E1E]">
                        {formatPrice(item?.price)}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleChangeOutfitItem(item)}
                        className="mt-1 font-spaceMono text-[10px] text-[#5C5C5C] hover:text-[#FF6333]"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {screen === FLOW_SCREENS.GENERATING ? (
            <div aria-live="polite">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-spaceMono text-[9px] uppercase tracking-[0.14em] text-[#8F333B]">
                    {isExpressTryOn
                      ? "Express Try-On"
                      : "Multi Try-On"}
                  </p>
                  <h2 className="mt-1 text-[27px] font-medium leading-tight text-[#1E1E1E] font-helvena sm:text-[34px]">
                    {isFinalTryOnImage
                      ? isExpressTryOn
                        ? "Your try-on is ready"
                        : "Your outfit is ready"
                      : isExpressTryOn
                        ? "Creating your try-on"
                        : "Creating your outfit"}
                  </h2>
                </div>
                {!multiTryOnStream?.done && !multiTryOnStream?.error ? (
                  <span className="mt-2 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#D9D5D0] border-t-[#8F333B]" />
                ) : null}
              </div>
              {multiTryOnStream?.error ? (
                <div className="mt-5 rounded-[6px] border border-red-200 bg-red-50 p-4" role="alert">
                  <p className="text-[12px] font-medium leading-[1.5] text-red-700">
                    {multiTryOnErrorMessage}
                  </p>
                </div>
              ) : (
                <>
                <div
                  className="mt-4 rounded-[6px] border border-[#E8E3DE] bg-[#FAF9F7] p-3"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <div className="flex items-start gap-2">
                    {!multiTryOnStream?.done ? (
                      <span className="mt-[6px] h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#8F333B]" />
                    ) : (
                      <span className="mt-[2px] grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[#8F333B] text-[9px] text-white">
                        ✓
                      </span>
                    )}
                    <div className="min-w-0 flex-1 text-[12px] leading-[1.55] text-[#34312E] font-helvena">
                      <MarkdownResponse message={currentStreamingMessage} />
                    </div>
                  </div>
                </div>
                  {streamedTryOnImage ? (
                    <div className="relative mt-4 overflow-hidden rounded-[6px] border border-[#E4DFDA] bg-[#EEEAE6]">
                      <img
                        src={streamedTryOnImage}
                        alt={
                          isFinalTryOnImage
                          ? isExpressTryOn
                            ? "Final Express Try-On"
                            : "Final multi-garment try-on"
                          : isExpressTryOn
                            ? "Partial Express Try-On preview"
                            : "Partial multi-garment try-on preview"
                        }
                        className="max-h-[360px] w-full object-contain"
                      />
                      <span className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 font-spaceMono text-[9px] uppercase tracking-[0.12em] text-white">
                        {isFinalTryOnImage ? "Final image" : "Partial preview"}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-4 grid min-h-[220px] place-items-center rounded-[6px] border border-dashed border-[#D7D1CB] bg-[#FAF9F7]">
                      <div className="text-center">
                        <span className="mx-auto block h-6 w-6 animate-spin rounded-full border-2 border-[#D7D1CB] border-t-[#8F333B]" />
                        <p className="mt-3 text-[11px] text-[#6B6661]">
                          Waiting for the first image preview...
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-[#EEEAE6] pt-3">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((step) => (
              <span
                key={step}
                className={[
                  "h-[7px] w-[7px] rounded-full",
                  step === currentStep ? "bg-[#252525]" : "bg-[#E4E4E4]",
                ].join(" ")}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            {screen !== FLOW_SCREENS.METHOD ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={isBusy}
                className="h-[44px] rounded-[8px] px-4 text-[12px] font-medium text-[#494540] transition hover:bg-[#F5F3F1] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Back
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleContinue}
              disabled={isContinueDisabled}
              className="group flex h-[40px] min-w-[180px] items-center justify-center gap-3 rounded-[8px] bg-[#8F333B] px-6 text-[13px] font-medium text-white transition-colors hover:opacity-[95%] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[190px]"
            >
              {isBusy ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  <span>Generating...</span>
                </>
              ) : screen === FLOW_SCREENS.GENERATING ? (
                <>
                  <span>{multiTryOnStream?.error ? "Back to review" : "Done"}</span>
                  <ContinueArrow />
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ContinueArrow />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VirtualTryOnFlowModal;
